import React, { createContext, useContext, useState, ReactNode } from 'react';
import Drawer from '../components/common/Drawer';
import Loader from '../components/common/Loader';
import FeedbackContent from '../components/drawer/FeedbackContent';

interface FeedbackDrawerContextType {
    // Feedback HTML is stored in blob storage, not returned inline with list/search results —
    // showFeedback takes a fetcher instead of the raw HTML and fetches it lazily when the drawer opens.
    showFeedback: (fetchFeedback: () => Promise<string | null | undefined>, title?: string) => void;
}

const FeedbackDrawerContext = createContext<FeedbackDrawerContextType | undefined>(undefined);

interface FeedbackDrawerProviderProps {
    children: ReactNode;
}

export const FeedbackDrawerProvider: React.FC<FeedbackDrawerProviderProps> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [html, setHtml] = useState('');
    const [title, setTitle] = useState('Feedback');
    const [isLoading, setIsLoading] = useState(false);

    const showFeedback = async (
        fetchFeedback: () => Promise<string | null | undefined>,
        feedbackTitle: string = 'Feedback',
    ) => {
        setTitle(feedbackTitle);
        setHtml('');
        setIsOpen(true);
        setIsLoading(true);

        try {
            const result = await fetchFeedback();
            setHtml(result ?? '');
        } catch {
            setHtml('<p>Unable to load feedback.</p>');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
        setHtml('');
    };

    return (
        <FeedbackDrawerContext.Provider value={{ showFeedback }}>
            {children}
            <Drawer isOpen={isOpen} onClose={handleClose} title={title} size="large">
                {isLoading ? <Loader /> : <FeedbackContent html={html} />}
            </Drawer>
        </FeedbackDrawerContext.Provider>
    );
};

export const useFeedbackDrawer = (): FeedbackDrawerContextType => {
    const context = useContext(FeedbackDrawerContext);
    if (context === undefined) {
        throw new Error('useFeedbackDrawer must be used within a FeedbackDrawerProvider');
    }
    return context;
};
